// src/components/auth/components/RegisterForm.tsx
import React, { useState, useEffect } from 'react';
import { COMPONENT_STYLES } from '@/theme';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import SocialButtons from './SocialButtons';
import Message from '@/components/ui/Message';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './AuthModal.module.css';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  selectedPlan?: string | null;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ 
  onSwitchToLogin,
  selectedPlan = null
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorKey, setFormErrorKey] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [apiErrorDetails, setApiErrorDetails] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { register, loginWithFacebook, loginWithGoogle, isLoading, error: authError } = useAuthContext();
  const { t } = useLanguage();
  useResponsive();

  // Effect for tracking auth errors and converting them to user-friendly messages
  useEffect(() => {
    if (authError) {
      const apiErrorStr = typeof authError === 'object' && 'api' in authError 
        ? String(authError.api || '') 
        : '';
      
      // Save technical details for debugging
      setApiErrorDetails(apiErrorStr);
      
      // Set user-friendly error messages using translation keys
      if (apiErrorStr.includes('уже существует') || 
          apiErrorStr.includes('already exists') ||
          apiErrorStr.includes('такой email')) {
        setFormErrorKey('auth.errors.email_exists');
      } else if (apiErrorStr.includes('password') && apiErrorStr.includes('validation')) {
        setFormErrorKey('auth.errors.password_requirements');
      } else if (apiErrorStr.includes('timeout') || apiErrorStr.includes('Network Error')) {
        setFormErrorKey('auth.errors.connection_error');
      } else if (typeof authError === 'object' && 'auth' in authError && authError.auth) {
        setFormError(authError.auth);
      } else if (typeof authError === 'string') {
        setFormError(authError);
      } else {
        setFormErrorKey('auth.errors.general');
      }
    }
  }, [authError]);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDebugInfo(t('common.debug.registration_process_start'));
    
    // Basic validation
    if (!email || !password) {
      setFormErrorKey('auth.errors.required_fields');
      setDebugInfo(t('auth.errors.required_fields'));
      return;
    }
    
    if (password !== confirmPassword) {
      setFormErrorKey('auth.errors.passwords_mismatch');
      setDebugInfo(t('auth.errors.passwords_mismatch'));
      return;
    }
    
    setFormError(null);
    setFormErrorKey(null);
    setDebugInfo(t('common.debug.sending_data'));
    
    try {
      // Prepare only necessary data for submission
      const registrationData = {
        email: email,
        password: password,
        // Если выбран тариф, добавляем его в данные регистрации
        ...(selectedPlan ? { selected_plan: selectedPlan } : {})
      };
      
      console.log(t('common.debug.sending_data_masked'), {
        ...registrationData,
        password: '********' // Hide password in logs
      });
      
      // Call registration function from context
      const success = await register(registrationData);
      
      setDebugInfo(`${t('common.debug.server_response')}: ${success ? t('common.yes') : t('common.no')}`);
      
      if (success) {
        // After successful registration, redirect to personal cabinet
        navigate('/personal');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      
      if (err.message) {
        if (err.message.includes('email') && 
           (err.message.includes('exists') || err.message.includes('уже существует'))) {
          setFormErrorKey('auth.errors.email_exists');
        } else if (err.message.includes('password')) {
          setFormErrorKey('auth.errors.password_requirements');
        } else {
          setFormErrorKey('auth.errors.general');
        }
      } else {
        setFormErrorKey('auth.errors.general');
      }
      
      setDebugInfo(`${t('common.error')}: ${err.message || t('auth.errors.general')}`);
    }
  };

  return (
    <div className={styles.authForm}>
      {/* Error message using Message component */}
      {formErrorKey && (
        <Message type="error" translateKey={formErrorKey} />
      )}
      
      {formError && !formErrorKey && (
        <Message type="error" message={formError} />
      )}
      
      {/* Debug information - visible only in development mode */}
      {process.env.NODE_ENV === 'development' && debugInfo && (
        <Message 
          type="debug" 
          message={
            <>
              <p className="font-bold">{t('common.debug.title')}:</p>
              <p>{debugInfo}</p>
              {apiErrorDetails && (
                <p className="mt-1 text-xs overflow-auto max-h-20">API Error: {apiErrorDetails}</p>
              )}
            </>
          }
        />
      )}
      
      <form onSubmit={handleRegisterSubmit} className="space-y-4">
        <div className={styles.formGroup}>
          <label 
            htmlFor="email" 
            className={styles.formLabel}
          >
            {t('auth.common.email')} *
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail((e.target as any).value)}
            className={COMPONENT_STYLES.input.base}
            placeholder={t('auth.register.email_placeholder') || "auth.register.email_placeholder"}
            disabled={isLoading}
            required
          />
        </div>
        
        <div className={styles.formGroup}>
          <label 
            htmlFor="password" 
            className={styles.formLabel}
          >
            {t('auth.common.password')} *
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword((e.target as any).value)}
              className={COMPONENT_STYLES.input.base}
              placeholder={t('auth.register.password_placeholder') || "auth.register.password_placeholder"}
              disabled={isLoading}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.common.hide_password') : t('auth.common.show_password')}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label 
            htmlFor="confirmPassword" 
            className={styles.formLabel}
          >
            {t('auth.register.confirm_password')} *
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword((e.target as any).value)}
              className={COMPONENT_STYLES.input.base}
              placeholder={t('auth.register.confirm_password_placeholder') || "auth.register.confirm_password_placeholder"}
              disabled={isLoading}
              required
            />
          </div>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full ${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary}`}
        >
          {isLoading ? t('auth.register.button_loading') : t('auth.register.button')}
        </button>
      </form>
      
      <div className={styles.formDivider}>
        <div className="flex justify-center">
          <span className={styles.formDividerText}>
            {t('auth.register.or_register_with')}
          </span>
        </div>
      </div>
      
      <SocialButtons 
        onFacebookLogin={loginWithFacebook}
        onGoogleLogin={loginWithGoogle}
      />
      
      <div className={styles.formFooter}>
        <p className="text-sm text-[#465357]">
          {t('auth.common.already_have_account')}{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className={styles.switchLink}
          >
            {t('auth.register.switch_to_login')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;